import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useApprovalStatus } from "@/hooks/useApprovalStatus";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useToast } from "@/hooks/use-toast";
import { Navbar } from "@/components/Navbar";
import { supabase } from "@/integrations/supabase/client";
import { Package, AlertCircle, CheckCircle, Plus, Pause, CalendarPlus } from "lucide-react";
import { validateFileUpload } from "@/lib/validation";
import type { Profile } from "@/types/database";
import { Link } from "react-router-dom";
import { PendingApproval } from "@/components/PendingApproval";
import { PickupRequestsSection } from "@/components/dashboard/PickupRequestsSection";
import { FeedbackSurveyModal } from "@/components/dashboard/FeedbackSurveyModal";
import { useSurveyPrompt } from "@/hooks/useSurveyPrompt";
import { SchedulePickupDialog } from "@/components/dashboard/SchedulePickupDialog";
import { ACTIVE_PICKUP_STATUSES } from "@/lib/pickupStatus";
import { PackageRowInput, createEmptyPackageRow, type PackageRowData } from "@/components/dashboard/PackageRowInput";

const MAX_PACKAGES_PER_BATCH = 10;

const Dashboard = () => {
  const { user, loading } = useAuth();
  const { isApproved, isPending, loading: approvalLoading } = useApprovalStatus();
  const { isAdmin } = useAdminStatus();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{ 
    subscribed: boolean; 
    paused?: boolean; 
    cancelAtPeriodEnd?: boolean; 
    expired?: boolean;
    expiredAt?: string;
  } | null>(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  
  // Shared fields
  const [notes, setNotes] = useState("");
  const [gatedAccess, setGatedAccess] = useState(false);
  const [accessInstructions, setAccessInstructions] = useState("");

  // Dynamic package rows
  const [packageRows, setPackageRows] = useState<PackageRowData[]>([createEmptyPackageRow()]);

  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [hasActivePickup, setHasActivePickup] = useState(false);

  // Survey prompt hook
  const {
    showSurvey,
    markSurveyCompleted,
    dismissSurvey,
    closeSurveyModal,
  } = useSurveyPrompt(user?.id);

  useEffect(() => {
    if (user) {
      fetchProfile();
      checkSubscription();
    }
  }, [user]);

  const checkSubscription = async () => {
    setCheckingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      setSubscriptionInfo(data);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error checking subscription:", error);
      }
      setSubscriptionInfo({ subscribed: false });
    } finally {
      setCheckingSubscription(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("*")
        .eq("id", user!.id)
        .maybeSingle();

      if (error) throw error;
      setProfile(data as Profile | null);
      
      if (data?.gate_code) {
        setGatedAccess(true);
        setAccessInstructions(data.gate_code);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching profile:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  const uploadFile = async (file: File, bucket: string) => {
    const validation = validateFileUpload(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${user!.id}/${Math.random()}.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fileName, file);

    if (uploadError) throw uploadError;
    return fileName;
  };

  const handlePackageRowChange = (index: number, data: PackageRowData) => {
    setPackageRows(prev => {
      const updated = [...prev];
      updated[index] = data;
      return updated;
    });
  };

  const handleAddPackage = () => {
    if (packageRows.length >= MAX_PACKAGES_PER_BATCH) {
      toast({
        title: "Maximum Reached",
        description: `You can submit up to ${MAX_PACKAGES_PER_BATCH} packages per batch.`,
        variant: "destructive",
      });
      return;
    }
    setPackageRows(prev => [...prev, createEmptyPackageRow()]);
  };

  const handleRemovePackage = (index: number) => {
    setPackageRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Subscription checks
    if (!isAdmin && !subscriptionInfo?.subscribed) {
      if (subscriptionInfo?.expired) {
        toast({ title: "Subscription Expired", description: "Your subscription has ended. Please resubscribe to continue scheduling pickups.", variant: "destructive" });
      } else {
        toast({ title: "Subscription Required", description: "Please subscribe to a plan before scheduling pickups.", variant: "destructive" });
      }
      navigate("/settings");
      return;
    }

    if (!isAdmin && subscriptionInfo?.paused) {
      toast({ title: "Subscription Paused", description: "Resume your subscription in Settings to submit packages.", variant: "destructive" });
      navigate("/settings");
      return;
    }

    // Validate address
    if (!profile?.street || !profile?.city || !profile?.zip || !profile?.state) {
      toast({ title: "Address Required", description: "Please add your pickup address in Settings before scheduling a pickup.", variant: "destructive" });
      return;
    }

    // Validate each package row
    for (let i = 0; i < packageRows.length; i++) {
      const row = packageRows[i];
      if (!row.retailer) {
        toast({ title: "Validation Error", description: `Package ${i + 1}: Please select a return location.`, variant: "destructive" });
        return;
      }
      if (!row.size) {
        toast({ title: "Validation Error", description: `Package ${i + 1}: Please select a size.`, variant: "destructive" });
        return;
      }
      if (!row.qrCodeFile) {
        toast({ title: "QR Code Required", description: `Package ${i + 1}: Please upload a QR code.`, variant: "destructive" });
        return;
      }
      if (!row.packagePhotoFile) {
        toast({ title: "Package Photo Required", description: `Package ${i + 1}: Please upload a photo of your package.`, variant: "destructive" });
        return;
      }
    }

    setSubmitting(true);

    try {
      // Check for active pickup
      const { data: activePickups } = await supabase
        .from("user_pickups")
        .select("id")
        .eq("user_id", user!.id)
        .in("status", ACTIVE_PICKUP_STATUSES as unknown as string[])
        .limit(1);

      const userHasActivePickup = (activePickups && activePickups.length > 0);

      // Upload files and insert each package individually
      for (const row of packageRows) {
        let qrCodeUrl = null;
        let packagePhotoUrl = null;

        if (row.qrCodeFile) {
          qrCodeUrl = await uploadFile(row.qrCodeFile, "qr-codes");
        }
        if (row.packagePhotoFile) {
          packagePhotoUrl = await uploadFile(row.packagePhotoFile, "package-photos");
        }

        const { error: packageError } = await (supabase as any)
          .from("packages")
          .insert({
            user_id: user!.id,
            retailer: row.retailer,
            size: row.size,
            city: profile?.city || "",
            qr_code_url: qrCodeUrl,
            package_photo_url: packagePhotoUrl,
            print_qr_needed: false,
            high_value: row.highValue,
            notes: notes || null,
            location_code: row.locationCode,
            status: "pending",
            return_by_date: row.returnByDate || null,
          });

        if (packageError) throw packageError;
      }

      setHasActivePickup(!!userHasActivePickup);
      setSubmittedCount(packageRows.length);

      toast({
        title: packageRows.length > 1 ? `${packageRows.length} Packages Submitted!` : "Package Submitted!",
        description: userHasActivePickup
          ? "Your packages have been submitted and linked to your current cycle."
          : "Your packages have been submitted. Schedule a pickup date now!",
      });

      // Reset form
      setNotes("");
      setPackageRows([createEmptyPackageRow()]);

      // Keep gated access from profile
      if (!profile?.gate_code) {
        setGatedAccess(false);
        setAccessInstructions("");
      }

      setSubmissionSuccess(true);

      if (!userHasActivePickup) {
        setShowScheduleDialog(true);
      }
    } catch (error: any) {
      toast({
        title: "Error submitting packages",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Show pending approval page
  if (!approvalLoading && user && isPending) {
    return <PendingApproval />;
  }

  // Show success state after submission
  if (submissionSuccess) {
    const userAddress = profile?.street && profile?.city && profile?.zip && profile?.state
      ? `${profile.street}, ${profile.city}, ${profile.zip}, ${profile.state}`
      : "";

    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <Navbar />
        <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
          <div className="max-w-lg mx-auto">
            <Card className="shadow-medium text-center">
              <CardContent className="py-12 px-6">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  {submittedCount > 1 ? `${submittedCount} Packages Submitted!` : "Package Submitted!"}
                </h2>
                <p className="text-muted-foreground mb-8">
                  {hasActivePickup
                    ? `Your ${submittedCount > 1 ? "packages have" : "package has"} been added to your current pickup cycle.`
                    : `Now let's schedule a pickup date for your ${submittedCount > 1 ? "packages" : "package"}.`}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {!hasActivePickup ? (
                    <Button
                      onClick={() => setShowScheduleDialog(true)}
                      className="min-h-[44px]"
                    >
                      <CalendarPlus className="w-4 h-4 mr-2" />
                      Schedule Pickup
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => setSubmissionSuccess(false)}
                        className="min-h-[44px]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Submit More Packages
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate("/tracking")}
                        className="min-h-[44px]"
                      >
                        View All Packages
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {user && (
          <SchedulePickupDialog
            open={showScheduleDialog}
            onOpenChange={setShowScheduleDialog}
            userId={user.id}
            userAddress={userAddress}
            approvedPackageCount={submittedCount}
            required={!hasActivePickup}
            onSuccess={() => {
              setShowScheduleDialog(false);
              navigate("/tracking");
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <Navbar />
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="max-w-3xl mx-auto">
          {/* Pickup Requests Section */}
          {user && (
            <PickupRequestsSection
              userId={user.id}
              subscriptionType={profile?.subscription_type || null}
            />
          )}

          <div className="mb-6 sm:mb-8 text-center">
            <h1 className="text-2xl sm:text-4xl font-bold mb-2">Schedule a Pickup</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Upload your QR code and package photo
            </p>
            {(subscriptionInfo?.subscribed || isAdmin) && (
              <p className="mt-2 text-sm font-medium text-primary">
                {isAdmin ? "Admin Access" : "Active Subscription"}
              </p>
            )}
          </div>

          {/* Subscription warnings */}
          {!checkingSubscription && !subscriptionInfo?.subscribed && !isAdmin && (
            <Card className="mb-4 sm:mb-6 border-destructive/50 bg-destructive/5">
              <CardContent className="py-3 sm:py-4 px-3 sm:px-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-destructive text-sm sm:text-base">
                      {subscriptionInfo?.expired ? "Subscription Expired" : "Subscription Required"}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {subscriptionInfo?.expired 
                        ? "Your subscription has ended. Resubscribe to schedule package pickups."
                        : "Subscribe to schedule package pickups."}
                    </p>
                  </div>
                  <Link to="/settings" className="w-full sm:w-auto">
                    <Button variant="default" size="sm" className="w-full sm:w-auto min-h-[44px]">
                      {subscriptionInfo?.expired ? "Resubscribe" : "Subscribe Now"}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {!checkingSubscription && subscriptionInfo?.subscribed && subscriptionInfo?.paused && !isAdmin && (
            <Card className="mb-4 sm:mb-6 border-amber-500/50 bg-amber-500/5">
              <CardContent className="py-3 sm:py-4 px-3 sm:px-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <Pause className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-700 text-sm sm:text-base">Subscription Paused</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Resume your subscription to submit new packages. Already confirmed pickups will still be completed.
                    </p>
                  </div>
                  <Link to="/settings" className="w-full sm:w-auto">
                    <Button variant="default" size="sm" className="w-full sm:w-auto min-h-[44px]">
                      Resume Subscription
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="shadow-medium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Package Information
              </CardTitle>
              <CardDescription>
                Submit up to {MAX_PACKAGES_PER_BATCH} packages — each with its own return location
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* Dynamic Package Rows */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Packages ({packageRows.length})</Label>
                  </div>

                  {packageRows.map((row, index) => (
                    <PackageRowInput
                      key={index}
                      index={index}
                      data={row}
                      onChange={handlePackageRowChange}
                      onRemove={handleRemovePackage}
                      canRemove={packageRows.length > 1}
                      totalCount={packageRows.length}
                    />
                  ))}

                  {packageRows.length < MAX_PACKAGES_PER_BATCH && packageRows.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleAddPackage}
                      className="w-full border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 min-h-[44px]"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Another Package ({packageRows.length}/{MAX_PACKAGES_PER_BATCH})
                    </Button>
                  )}
                </div>

                {/* Shared: Address */}
                <div className="space-y-2">
                  <Label htmlFor="address">Pickup Address</Label>
                  <div className="p-3 border rounded-md bg-muted/50">
                    <p className="text-sm">
                      {profile?.street && profile?.city && profile?.zip && profile?.state
                        ? `${profile.street}, ${profile.city}, ${profile.state} ${profile.zip}`
                        : "No address saved. Please add your address in Settings."}
                    </p>
                    {profile?.gate_code && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Gate Code: <span className="font-medium text-foreground">{profile.gate_code}</span>
                      </p>
                    )}
                  </div>
                  {!profile?.street && (
                    <p className="text-xs text-muted-foreground">
                      Update your address in Settings → Personal Information
                    </p>
                  )}
                </div>

                {/* Shared: Gated Access */}
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Gated Access</Label>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">
                        Does your pickup location have gated access?
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant={gatedAccess ? "default" : "outline"}
                          size="sm"
                          onClick={() => setGatedAccess(true)}
                        >
                          Yes
                        </Button>
                        <Button
                          type="button"
                          variant={!gatedAccess ? "default" : "outline"}
                          size="sm"
                          onClick={() => { setGatedAccess(false); setAccessInstructions(""); }}
                        >
                          No
                        </Button>
                      </div>
                    </div>
                  </div>

                  {gatedAccess && (
                    <div className="space-y-2">
                      <Label htmlFor="accessInstructions">Access Code or Instructions</Label>
                      <Input
                        id="accessInstructions"
                        placeholder="Enter gate code or access instructions"
                        value={accessInstructions}
                        onChange={(e) => setAccessInstructions(e.target.value)}
                        maxLength={200}
                      />
                      <p className="text-xs text-muted-foreground">
                        {accessInstructions.length}/200 characters
                      </p>
                    </div>
                  )}
                </div>

                {/* Shared: Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Input
                    id="notes"
                    placeholder="Any special instructions?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={200}
                  />
                  <p className="text-xs text-muted-foreground">
                    {notes.length}/200 characters
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting
                    ? "Submitting..."
                    : packageRows.length > 1
                      ? `Submit ${packageRows.length} Packages`
                      : "Submit Package"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Feedback Survey Modal */}
      <FeedbackSurveyModal
        open={showSurvey}
        onOpenChange={closeSurveyModal}
        onTakeSurvey={markSurveyCompleted}
        onMaybeLater={dismissSurvey}
      />
    </div>
  );
};

export default Dashboard;
